import parse from 'html-react-parser';
import './Onboarding.css';
import { useCallback, useEffect, useState } from 'react';
import PlaceHolder from '../components/PlaceHolder';
import {
  AppIdentity,
  errorMessages,
  initialFormValues,
  
} from '../utilities/constants';
import Dialog from '../components/Dialog';
import AppDetails from '../components/AppDetails';
import OnboardingForm from '../components/OnboardingForm';
import {
  fetchPricingDetails,
  getAppIdandSPNData,
  getAppList,
  getModelsForOnboardingApps
} from '../service/apiServices';
import { useAzureData } from '../context/AzureContext';
import PricingModel from '../components/PricingModel';

type SPNAppIdEnvironmentMap = {
  environment: string;
  service_principal_name: string;
  application_id: string;
};
type ModelDetails = {
  model_id: string;
  model_name: string;
  prod: string;
  'non-prod': string;
};
type OnboardingFormValue = {
  onboarding_id?: number;
  environment: string[];
  application_name: string;
  model_name: string[];
  model_id: string[];
  primary_owner: string;
  wl3_approver: string;
  application_description: string;
  secondary_owner: string;
  icc: string;
  cc: string;
  aif_rag_access: boolean;
  ai_inventory_id: string;
  onboardingstatus: string | null;
  spn_appid_env_map: SPNAppIdEnvironmentMap[] | [];
  adgroupforappaccess: string | string[];
};

type TierDescription = {
  pricing: string;
  includes: [];
  notes: [];
};

type PricingDetails = {
  tier: string;
  tierdescription: {
    tierDescription: TierDescription;
  };
};

const CACHE_KEY = 'spnOptionsCache';
const CACHE_TTL = 1000 * 60 * 60 * 24;
function Onboarding() {
  const [showAppList, setShowAppList] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showEditableForm, setShowEditableForm] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<any>(null);
  const [onboardedApps, setOnboardedApps] = useState<OnboardingFormValue[] | []>([]);
  const [modelDetails, setModelDetails] = useState<ModelDetails[] | []>([]);
  const [spnOptions, setSPNOptions] = useState<AppIdentity[] | []>();
  const [prefilledData, setPrefilledData] = useState<OnboardingFormValue | {}>({});
  const [showPricingModel, setShowPricingModel] = useState(true);
  const { apiToken, isAdmin } = useAzureData();
  const [tierInfo, setTierInfo] = useState('');
  const [pricingDetails, setPricingDetails] = useState<PricingDetails[] | []>([]);

  const handleShowListofApps = () => {
    setShowAppList(true);
    setShowPricingModel(false);
  };
  const getModels = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getModelsForOnboardingApps(apiToken);
      setModelDetails(response);
    } catch (error: any) {
      setErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadData = async () => {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Date.now() < parsed.expiry) {
        setSPNOptions(parsed.value);
        return;
      }
    }

    try {
      setIsLoading(true);
      const data: AppIdentity[] = await getAppIdandSPNData(apiToken);
      setSPNOptions(data);
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ value: data, expiry: Date.now() + CACHE_TTL })
      );
    } catch (error: any) {
      setErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackClick = () => {
    setShowAppList(false);
    setShowEditableForm(false);
    setShowPricingModel(true);
  };

  const handleEdit = (id: number) => {
    setShowAppList(false);
    setShowEditableForm(true);
    const editableFormValue = onboardedApps.filter((app) => app.onboarding_id === id)[0];
    setPrefilledData(editableFormValue);
  };

  useEffect(() => {
    loadData();
  }, []);
  useEffect(() => {
    getOnboardedApps();
    getPricingDetails();
  }, [showEditableForm]);

  useEffect(() => {
    getModels();
  }, [getModels]);

  const convertValueintoArray = (result: any) => {
    const updatedData = result.map((item: any) => ({
      ...item,
      environment: [item.environment],
      adgroupforappaccess: Array.isArray(item.adgroupforappaccess)
        ? item.adgroupforappaccess
        : item.adgroupforappaccess.split(',')
    }));
    setOnboardedApps(updatedData);
  };

  const getOnboardedApps = async () => {
    try {
      setIsLoading(true);
      const result = await getAppList(apiToken);
       convertValueintoArray(result.onboarding_data);
      // convertValueintoArray(response.onboarding_data);
    } catch (error) {
      setErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPricingDetails = async () => {
    try {
      const response = await fetchPricingDetails(apiToken);
      setPricingDetails(response.pricing_model_details);
    } catch (error) {
      setErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };
  if (errorMessage) {
    if (errorMessage.message === 'UNAUTHORIZED') return <Dialog />;
    else {
      return <PlaceHolder message={parse(errorMessages.serverError)} />;
    }
  }
  if (isLoading)
    return (
      <div
        style={{ height: '100vh' }}
        className="container-fluid d-flex justify-content-center align-items-center">
        <h4 className="text-center">Loading...</h4>
      </div>
    );

  const renderprefilledForm = () => {
    return (
      <OnboardingForm
        formData={prefilledData}
        listOfModels={modelDetails}
        buttonLabal={'Update'}
        listofAppIdentities={spnOptions}
        unEditableFields={true}
        pricingDetails={pricingDetails}
        handleNavigation={() => setShowPricingModel((prevState) => !prevState)}
      />
    );
  };
  const renderForm = () => {
    return (
      <OnboardingForm
        formData={initialFormValues(tierInfo)}
        listOfModels={modelDetails}
        buttonLabal={'Submit'}
        listofAppIdentities={spnOptions}
        unEditableFields={false}
        pricingDetails={pricingDetails}
        handleNavigation={() => setShowPricingModel((prevState) => !prevState)}
      />
    );
  };

  const renderButton = () => {
    return (
      <div className="row justify-content-end">
        <div className="col-lg-3">
          <button
            className="btn uniBtnYellow"
            onClick={handleShowListofApps}
            style={{ height: '42px' }}>
            Show All App
          </button>
        </div>
      </div>
    );
  };
  const handleTier = (tier: string) => {
    setShowPricingModel(false);
    setTierInfo(tier);
  };
  const renderListOfApps = () => {
    return (
      <AppDetails onBackClick={handleBackClick} onEditClick={handleEdit} appList={onboardedApps} />
    );
  };
  return (
    <>
      <div className="onboarding-container">
        <div className="row text-center">
          {!showAppList && (
            <>
              <div className={` ${showPricingModel && isAdmin ? 'col-md-12' : 'col-md-10'}`}>
                <h3>AI Foundation Onboarding</h3>
              </div>
              <div>{isAdmin && renderButton()}</div>

              {showPricingModel && (
                <div className="row mt-5">
                  {pricingDetails.map((data, index) => {
                    return (
                      <div className="col-md mt-3" key={`${data.tier}-${index}`}>
                        <PricingModel
                          tier={data.tier}
                          tierDescription={data.tierdescription.tierDescription}
                          handleTier={handleTier}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
        {!showPricingModel && (
          <>
            {showAppList && renderListOfApps()}
            {!showAppList && !showEditableForm && !showPricingModel && renderForm()}
            {showEditableForm && !showAppList && renderprefilledForm()}
          </>
        )}
      </div>
    </>
  );
}

export default Onboarding;
